import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Blockquote from '../../../components/Blockquote';
import Link from '../../../components/Link';
import NumberInput from '../../../components/NumberInput/NumberInput';
import { RadioGroup } from '../../../components/Radio';
import RadioWithImage from '../../../components/RadioWithImage';
import CustomSelect from '../../../components/Select';
import Support from '../../../components/Support/Support';
import SupportMobile from '../../../components/Support/SupportMobile';

const radioCards = [
  {
    imageRef: 'savings-enrollment-no.png',
    cardTitle: 'No',
    cardSubtitle: 'I don’t want to schedule increases at this time',
    id: 'savings-enrollment-no',
  },
  {
    imageRef: 'savings-enrollment-yes.png',
    cardTitle: 'Yes',
    cardSubtitle: 'I would like to increase my contributions over time',
    id: 'savings-enrollment-yes',
  },
];

export type StepProps = {
  data: any;
  step1Data: any;
  collectStepData: (data: any) => void;
};

const SavingsAutoIncrease = ({
  data,
  step1Data,
  collectStepData,
}: StepProps) => {
  const [value, setValue] = useState<string | number>('');
  const [pickedMonth, setPickedMonth] = useState<number>();

  const [pickedYear, setPickedYear] = useState<number>();

  const [pickedPeriod, setPickedPeriod] = useState<number>();

  const handleChange = (e: string | number) => {
    setValue(e);
  };

  const handleMonthChange = (e: any) => {
    setPickedMonth(e);
  };

  const handleYearChange = (e: any) => {
    setPickedYear(e);
  };

  const handlePeriodChange = (e: any) => {
    setPickedPeriod(e);
  };

  const handleBeforeTaxSavingsGoalOption = (e: number | null | undefined) => {
    collectStepData({
      ...data,
      beforeTax: {
        ...data.beforeTax,
        savingsGoal: e,
      },
    });
  };

  const handleBeforeTaxIncreaseByOption = (e: number | null | undefined) => {
    collectStepData({
      ...data,
      beforeTax: {
        ...data.beforeTax,
        increaseBy: e,
      },
    });
  };

  const handleRoth401SavingsGoalOption = (e: number | null | undefined) => {
    collectStepData({
      ...data,
      roth401: {
        ...data.roth401,
        savingsGoal: e,
      },
    });
  };

  const handleRoth401IncreaseByOption = (e: number | null | undefined) => {
    collectStepData({
      ...data,
      roth401: {
        ...data.roth401,
        increaseBy: e,
      },
    });
  };

  const handleAfterTaxSavingsGoalOption = (e: number | null | undefined) => {
    collectStepData({
      ...data,
      afterTax: {
        ...data.afterTax,
        savingsGoal: e,
      },
    });
  };

  const handleAfterTaxIncreaseByOption = (e: number | null | undefined) => {
    collectStepData({
      ...data,
      afterTax: {
        ...data.afterTax,
        increaseBy: e,
      },
    });
  };

  const handleBeforeTaxCatchUpSavingsGoalOption = (
    e: number | null | undefined
  ) => {
    collectStepData({
      ...data,
      beforeTaxCatchUp: {
        ...data.beforeTaxCatchUp,
        savingsGoal: e,
      },
    });
  };

  const handleBeforeTaxCatchUpIncreaseByOption = (
    e: number | null | undefined
  ) => {
    collectStepData({
      ...data,
      beforeTaxCatchUp: {
        ...data.beforeTaxCatchUp,
        increaseBy: e,
      },
    });
  };

  const handleRoth401CatchUpSavingsGoalOption = (
    e: number | null | undefined
  ) => {
    collectStepData({
      ...data,
      roth401CatchUp: {
        ...data.roth401CatchUp,
        savingsGoal: e,
      },
    });
  };

  const handleRoth401CatchUpIncreaseByOption = (
    e: number | null | undefined
  ) => {
    collectStepData({
      ...data,
      roth401CatchUp: {
        ...data.roth401CatchUp,
        increaseBy: e,
      },
    });
  };

  const months = [
    { value: '0', label: 'January' },
    { value: '1', label: 'February' },
    { value: '2', label: 'March' },
    { value: '3', label: 'April' },
    { value: '4', label: 'May' },
    { value: '5', label: 'June' },
    { value: '6', label: 'July' },
    { value: '7', label: 'August' },
    { value: '8', label: 'September' },
    { value: '9', label: 'October' },
    { value: '10', label: 'November' },
    { value: '11', label: 'December' },
  ];

  const years = [
    { value: '2023', label: '2023' },
    { value: '2024', label: '2024' },
    { value: '2025', label: '2025' },
    { value: '2026', label: '2026' },
    { value: '2027', label: '2027' },
    { value: '2028', label: '2028' },
    { value: '2029', label: '2029' },
    { value: '2030', label: '2030' },
  ];

  const contribution = [
    { value: '3', label: 'Quarterly' },
    { value: '6', label: 'Semi-Annually' },
    { value: '1', label: 'Annually' },
  ];

  const handleDateCalculate = (
    month?: any,
    year?: any,
    period?: any,
    savingsGoal?: any,
    increaseBy?: any,
    currentSaving?: any
  ) => {
    if (!month || !year || !period || !savingsGoal || !increaseBy) return false;

    let currentMonth = +month;
    let currentYear = +year;
    let currentDay = new Date().getDate();

    const currentSavingGoal = currentSaving;
    const newSavingGoal = savingsGoal;
    const percentsToReachGoal = currentSavingGoal
      ? newSavingGoal - currentSavingGoal
      : newSavingGoal;
    const percentIncreaseOnOnePeriod = increaseBy;
    const periodMultiplayer = Math.floor(
      percentsToReachGoal / percentIncreaseOnOnePeriod
    );
    const periodToAdd = period * periodMultiplayer;

    if (+period === 3 || +period === 6) {
      const calculatedDate = new Date(
        new Date(currentYear, currentMonth, currentDay).setMonth(
          new Date(currentYear, currentMonth, currentDay).getMonth() +
            periodToAdd
        )
      ).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      return calculatedDate.split(' ').join(', ');
    } else if (+period === 1) {
      const calculatedDate = new Date(
        new Date(currentYear, currentMonth, currentDay).setFullYear(
          new Date(currentYear, currentMonth, currentDay).getFullYear() +
            periodToAdd
        )
      ).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      return calculatedDate.split(' ').join(', ');
    }
  };

  const calculateBeforeTaxGoalReachDate = useMemo(
    () =>
      handleDateCalculate(
        pickedMonth,
        pickedYear,
        pickedPeriod,
        data.beforeTax.savingsGoal,
        data.beforeTax.increaseBy,
        step1Data.beforeTax
      ),
    [
      pickedMonth,
      pickedYear,
      pickedPeriod,
      data.beforeTax.savingsGoal,
      data.beforeTax.increaseBy,
      step1Data.beforeTax,
    ]
  );

  const calculateRoth401GoalReachDate = useMemo(
    () =>
      handleDateCalculate(
        pickedMonth,
        pickedYear,
        pickedPeriod,
        data.roth401.savingsGoal,
        data.roth401.increaseBy,
        step1Data.roth401
      ),
    [
      pickedMonth,
      pickedYear,
      pickedPeriod,
      data.roth401.savingsGoal,
      data.roth401.increaseBy,
      step1Data.roth401,
    ]
  );

  const calculateAfterTaxGoalReachDate = useMemo(
    () =>
      handleDateCalculate(
        pickedMonth,
        pickedYear,
        pickedPeriod,
        data.afterTax.savingsGoal,
        data.afterTax.increaseBy,
        step1Data.afterTax
      ),
    [
      pickedMonth,
      pickedYear,
      pickedPeriod,
      data.afterTax.savingsGoal,
      data.afterTax.increaseBy,
      step1Data.afterTax,
    ]
  );

  const calculateBeforeTaxCatchUpGoalReachDate = useMemo(
    () =>
      handleDateCalculate(
        pickedMonth,
        pickedYear,
        pickedPeriod,
        data.beforeTaxCatchUp.savingsGoal,
        data.beforeTaxCatchUp.increaseBy,
        step1Data.beforeTaxCatchUp
      ),
    [
      pickedMonth,
      pickedYear,
      pickedPeriod,
      data.beforeTaxCatchUp.savingsGoal,
      data.beforeTaxCatchUp.increaseBy,
      step1Data.beforeTaxCatchUp,
    ]
  );

  const calculateRoth401CatchUpCatchUpGoalReachDate = useMemo(
    () =>
      handleDateCalculate(
        pickedMonth,
        pickedYear,
        pickedPeriod,
        data.roth401CatchUp.savingsGoal,
        data.roth401CatchUp.increaseBy,
        step1Data.roth401CatchUp
      ),
    [
      pickedMonth,
      pickedYear,
      pickedPeriod,
      data.roth401CatchUp.savingsGoal,
      data.roth401CatchUp.increaseBy,
      step1Data.roth401CatchUp,
    ]
  );

  return (
    <Container className="p-0 d-flex flex-row">
      <div className={'col-12 col-lg-8'}>
        <section className={'col-12'}>
          <div className="d-flex flex-row justify-content-between">
            <h5 className={'mt-12 mb-lg-9 text-sm-20-lg-24'}>
              Would you like to increase your contributions over time?
            </h5>
            <div className={'mt-12 text-sm-20-lg-24 d-lg-none'}>
            <SupportMobile>
                <div className="p-0 mt-3">
                  <span className="fw-light text-sub1">
                    Here are a few things that will guide you to make the best
                    decision
                  </span>
                  <Link
                    rightIcon={light('chevron-right')}
                    onSelect={(selectedKey) =>
                      console.log(`selected ${selectedKey}`)
                    }
                    eventKey="test"
                    className="pt-3"
                    isAlignAtEnd
                    isBlue
                  >
                    <span className="text-caption lh-xs">Tax Breakdown</span>
                  </Link>
                  <Link
                    rightIcon={light('chevron-right')}
                    onSelect={(selectedKey) =>
                      console.log(`selected ${selectedKey}`)
                    }
                    eventKey="test"
                    className="pt-3"
                    isAlignAtEnd
                    isBlue
                  >
                    <span className="text-caption lh-xs">Min/Max Chart</span>
                  </Link>
                  <Link
                    rightIcon={light('chevron-right')}
                    onSelect={(selectedKey) =>
                      console.log(`selected ${selectedKey}`)
                    }
                    eventKey="test"
                    className="pt-3"
                    isAlignAtEnd
                    isBlue
                  >
                    <span className="text-caption lh-xs">
                      Contribution Calculator
                    </span>
                  </Link>
                </div>
              </SupportMobile>
            </div>
          </div>
          <div
            className={
              'mt-11 mt-lg-12 fw-light text-fs-6 lh-xs text-ligther-grey'
            }
          >
            <Blockquote>
              <div>
                <div className="m-0 text-sub1 fw-bolder text-ligther-grey">
                  <span>Did You Know?</span>
                </div>
                <div className="fw-light text-caption lh-xs pt-3 d-flex align-items-center">
                  <span>
                    The average person sets their contribution once and never
                    makes any changes. If you schedule an increase now, you can
                    slowly increase the amount you are contributing over time.
                    If you aren't receiving the full company match today, you
                    can ensure you get there over time. Don't leave free money
                    on the table.
                  </span>
                </div>
              </div>
            </Blockquote>
          </div>
        </section>
        <section className="mt-13">
          <RadioGroup name={'no-labels'} value={value} onChange={handleChange}>
            <Row className="d-flex flex-column flex-lg-row m-0">
              {radioCards.map((radioCard, i) => {
                return (
                  <Col
                    key={i}
                    className={`col-12 col-lg-4 mb-10 p-0 ${
                      i === 0 ? 'me-lg-10' : ''
                    }`}
                  >
                    <RadioWithImage
                      key={i}
                      cardData={radioCard}
                      selected={value !== radioCard.id}
                    />
                  </Col>
                );
              })}
            </Row>
          </RadioGroup>
        </section>
        {value === 'savings-enrollment-yes' && (
          <section className="col-12 px-10 pt-11 pb-10 bg-white rounded-1">
            <div>
              <h5 className={'text-sm-20-lg-24 lh-xs'}>
                When would you like your initial increase to occur?
              </h5>
              <div className="mt-7 d-lg-flex">
                <div>
                  <CustomSelect
                    options={months}
                    className="col-lg-4"
                    onChange={handleMonthChange}
                  />
                </div>
                <div className="ms-lg-5">
                  <CustomSelect
                    options={years}
                    className="mt-3 mt-lg-0 col-lg-4"
                    onChange={handleYearChange}
                  />
                </div>
              </div>
            </div>
            <div className="mt-11">
              <h5 className={'text-sm-20-lg-24 lh-xs'}>
                How often would you like to increase your contribution?
              </h5>
              <div className="text-fs-6 lh-xs fw-light">
                <span>
                  (The increases occur based upon the frequency you select.)
                </span>
              </div>
              <div className="mt-7">
                <CustomSelect
                  options={contribution}
                  className="col-lg-4"
                  onChange={handlePeriodChange}
                />
              </div>
            </div>
            <div className="mt-11">
              <h5 className={'text-sm-20-lg-24 lh-xs'}>
                Follow these steps to complete your auto increase
              </h5>
              <div className="text-fs-6 fw-light">
                <div className="pt-3 d-flex align-items-start">
                  <span className="text-success">
                    <FontAwesomeIcon icon={light('check')} className="pe-3" />
                  </span>
                  <Link
                    className="py-0"
                    rightIcon={light('chevron-right')}
                    isBlue
                  >
                    Set your savings goal for each contribution type you want to
                    increase
                  </Link>
                </div>
                <div className="pt-3 d-flex align-items-start">
                  <span className="text-success">
                    <FontAwesomeIcon icon={light('check')} className="pe-3" />
                  </span>
                  <Link
                    className="py-0"
                    rightIcon={light('chevron-right')}
                    isBlue
                  >
                    Set the amount you want to increase each time your
                    contribution changes
                  </Link>
                </div>
                <div className="pt-3 d-flex align-items-start">
                  <span className="text-success">
                    <FontAwesomeIcon icon={light('check')} className="pe-3" />
                  </span>
                  <Link
                    className="py-0"
                    rightIcon={light('chevron-right')}
                    isBlue
                  >
                    Skip entries for contribution types you don't want to change
                  </Link>
                </div>
                <div className="pt-3 d-flex align-items-start">
                  <span className="text-success">
                    <FontAwesomeIcon icon={light('check')} className="pe-3" />
                  </span>

                  <Link
                    className="py-0"
                    rightIcon={light('chevron-right')}
                    isBlue
                  >
                    Increases will stop when your goals are met
                  </Link>
                </div>
              </div>
            </div>
            <div className="mt-12">
              <Row className={`m-0 mb-7 border-bottom pb-7 flex-lg-column`}>
                <Col
                  className={`p-0 flex-lg-row
                d-flex flex-column justify-content-between mb-lg-7 pb-lg-7 border-bottom-desktop`}
                >
                  <div className="text-light-grey-normal text-sub1 col-lg-5">
                    <span>Current Elections</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Savings goal</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Increase by</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1">
                    <span>Goal reached on</span>
                  </div>
                </Col>
                <Col
                  className={`p-0 d-flex flex-column justify-content-between flex-lg-row
                `}
                >
                  <div
                    className={`d-flex flex-row justify-content-between align-items-lg-center col-lg-5`}
                  >
                    <span
                      className="text-sub col-lg-7"
                      style={{ maxWidth: 85 }}
                    >
                      {data.beforeTax.title}
                    </span>
                    <span className="text-sub1 fw-light col-lg-5">
                      {step1Data.beforeTax || 0}%
                    </span>
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={
                        step1Data.beforeTax
                          ? step1Data.beforeTax.toString()
                          : '0'
                      }
                      maxValue={'100'}
                      value={
                        data.beforeTax.savingsGoal || step1Data.beforeTax || 0
                      }
                      onChange={(e) => handleBeforeTaxSavingsGoalOption(e)}
                    />
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={'0'}
                      maxValue={
                        (
                          data.beforeTax.savingsGoal ||
                          0 - step1Data.beforeTax ||
                          0
                        ).toString() || '100'
                      }
                      value={data.beforeTax.increaseBy || 0}
                      onChange={(e) => handleBeforeTaxIncreaseByOption(e)}
                    />
                  </div>
                  <div className={`pt-7 text-sub1 fw-light text-end col-lg-2`}>
                    <span>{calculateBeforeTaxGoalReachDate}</span>
                  </div>
                </Col>
              </Row>
              <Row className={`m-0 pb-7 mb-7 border-bottom`}>
                <Col
                  className={`p-0 d-flex d-lg-none flex-column justify-content-between mb-lg-7 pb-lg-7 border-bottom-desktop`}
                >
                  <div className="text-light-grey-normal text-sub1 col-lg-5">
                    <span>Current Elections</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Savings goal</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Increase by</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1">
                    <span>Goal reached on</span>
                  </div>
                </Col>
                <Col
                  className={`p-0 d-flex flex-column justify-content-between flex-lg-row
                `}
                >
                  <div
                    className={`d-flex flex-row justify-content-between align-items-lg-center col-lg-5`}
                  >
                    <span
                      className="text-sub col-lg-7"
                      style={{ maxWidth: 85 }}
                    >
                      {data.roth401.title}
                    </span>
                    <span className="text-sub1 fw-light col-lg-5">
                      {step1Data.roth401 || 0}%
                    </span>
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={
                        step1Data.roth401 ? step1Data.roth401.toString() : '0'
                      }
                      maxValue={'100'}
                      value={data.roth401.savingsGoal || step1Data.roth401 || 0}
                      onChange={(e) => handleRoth401SavingsGoalOption(e)}
                    />
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={'0'}
                      maxValue={
                        (
                          data.roth401.savingsGoal ||
                          0 - step1Data.roth401 ||
                          0
                        ).toString() || '100'
                      }
                      value={data.roth401.increaseBy || 0}
                      onChange={(e) => handleRoth401IncreaseByOption(e)}
                    />
                  </div>
                  <div className={`pt-7 text-sub1 fw-light text-end col-lg-2`}>
                    <span>{calculateRoth401GoalReachDate}</span>
                  </div>
                </Col>
              </Row>
              <Row className={`m-0 pb-7 mb-7 border-bottom`}>
                <Col
                  className={`p-0 d-flex d-lg-none flex-column justify-content-between mb-lg-7 pb-lg-7 border-bottom-desktop`}
                >
                  <div className="text-light-grey-normal text-sub1 col-lg-5">
                    <span>Current Elections</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Savings goal</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Increase by</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1">
                    <span>Goal reached on</span>
                  </div>
                </Col>
                <Col
                  className={`p-0 d-flex flex-column justify-content-between flex-lg-row
                `}
                >
                  <div
                    className={`d-flex flex-row justify-content-between align-items-lg-center col-lg-5`}
                  >
                    <span
                      className="text-sub col-lg-7"
                      style={{ maxWidth: 85 }}
                    >
                      {data.afterTax.title}
                    </span>
                    <span className="text-sub1 fw-light col-lg-5">
                      {step1Data.afterTax || 0}%
                    </span>
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={
                        step1Data.afterTax ? step1Data.afterTax.toString() : '0'
                      }
                      maxValue={'100'}
                      value={
                        data.afterTax.savingsGoal || step1Data.afterTax || 0
                      }
                      onChange={(e) => handleAfterTaxSavingsGoalOption(e)}
                    />
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={'0'}
                      maxValue={
                        (
                          data.afterTax.savingsGoal ||
                          0 - step1Data.afterTax ||
                          0
                        ).toString() || '100'
                      }
                      value={data.afterTax.increaseBy || 0}
                      onChange={(e) => handleAfterTaxIncreaseByOption(e)}
                    />
                  </div>
                  <div className={`pt-7 text-sub1 fw-light text-end col-lg-2`}>
                    <span>{calculateAfterTaxGoalReachDate}</span>
                  </div>
                </Col>
              </Row>
              <Row className={`m-0 pb-7 mb-7 border-bottom`}>
                <Col
                  className={`p-0 d-flex d-lg-none flex-column justify-content-between mb-lg-7 pb-lg-7 border-bottom-desktop`}
                >
                  <div className="text-light-grey-normal text-sub1 col-lg-5">
                    <span>Current Elections</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Savings goal</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Increase by</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1">
                    <span>Goal reached on</span>
                  </div>
                </Col>
                <Col
                  className={`p-0 d-flex flex-column justify-content-between flex-lg-row
                `}
                >
                  <div
                    className={`d-flex flex-row justify-content-between align-items-lg-center col-lg-5`}
                  >
                    <span
                      className="text-sub col-lg-7"
                      style={{ maxWidth: 85 }}
                    >
                      {data.beforeTaxCatchUp.title}
                    </span>
                    <span className="text-sub1 fw-light col-lg-5">
                      {step1Data.beforeTaxCatchUp || 0}%
                    </span>
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={
                        step1Data.beforeTaxCatchUp
                          ? step1Data.beforeTaxCatchUp.toString()
                          : '0'
                      }
                      maxValue={'100'}
                      value={
                        data.beforeTaxCatchUp.savingsGoal ||
                        step1Data.beforeTaxCatchUp ||
                        0
                      }
                      onChange={(e) =>
                        handleBeforeTaxCatchUpSavingsGoalOption(e)
                      }
                    />
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={'0'}
                      maxValue={
                        (
                          data.beforeTaxCatchUp.savingsGoal ||
                          0 - step1Data.beforeTaxCatchUp ||
                          0
                        ).toString() || '100'
                      }
                      value={data.beforeTaxCatchUp.increaseBy || 0}
                      onChange={(e) =>
                        handleBeforeTaxCatchUpIncreaseByOption(e)
                      }
                    />
                  </div>
                  <div className={`pt-7 text-sub1 fw-light text-end col-lg-2`}>
                    <span>{calculateBeforeTaxCatchUpGoalReachDate}</span>
                  </div>
                </Col>
              </Row>
              <Row className={`m-0 mb-0 border-bottom-mobile pb-7`}>
                <Col
                  className={`p-0 d-lg-none d-flex flex-column justify-content-between mb-lg-7 pb-lg-7 border-bottom-desktop`}
                >
                  <div className="text-light-grey-normal text-sub1 col-lg-5">
                    <span>Current Elections</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Savings goal</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1 col-lg-2">
                    <span>Increase by</span>
                  </div>
                  <div className="pt-7 pt-lg-0 text-light-grey-normal text-sub1">
                    <span>Goal reached on</span>
                  </div>
                </Col>
                <Col
                  className={`p-0 d-flex flex-column justify-content-between flex-lg-row
                `}
                >
                  <div
                    className={`d-flex flex-row justify-content-between align-items-lg-center col-lg-5`}
                  >
                    <span
                      className="text-sub col-lg-7"
                      style={{ maxWidth: 85 }}
                    >
                      {data.roth401CatchUp.title}
                    </span>
                    <span className="text-sub1 fw-light col-lg-5">
                      {step1Data.roth401CatchUp || 0}%
                    </span>
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={
                        step1Data.roth401CatchUp
                          ? step1Data.roth401CatchUp.toString()
                          : '0'
                      }
                      maxValue={'100'}
                      value={
                        data.roth401CatchUp.savingsGoal ||
                        step1Data.roth401CatchUp ||
                        0
                      }
                      onChange={(e) => handleRoth401CatchUpSavingsGoalOption(e)}
                    />
                  </div>
                  <div className="pt-7 pt-lg-0 col-lg-2">
                    <NumberInput
                      placeholder={'0'}
                      isPercent
                      minValue={'0'}
                      maxValue={
                        (
                          data.roth401CatchUp.savingsGoal ||
                          0 - step1Data.roth401CatchUp ||
                          0
                        ).toString() || '100'
                      }
                      value={data.roth401CatchUp.increaseBy || 0}
                      onChange={(e) => handleRoth401CatchUpIncreaseByOption(e)}
                    />
                  </div>
                  <div className={`pt-7 text-sub1 fw-light text-end col-lg-2`}>
                    <span>{calculateRoth401CatchUpCatchUpGoalReachDate}</span>
                  </div>
                </Col>
              </Row>
            </div>
          </section>
        )}
      </div>
      <div className="d-none d-lg-block col-lg-4">
        <section className="mt-12">
        <Support>
            <div className='p-0'>
              <span className="fw-light text-sub1">
                Here are a few things that will guide you to make the best
                decision
              </span>
              <Link
                rightIcon={light('chevron-right')}
                onSelect={(selectedKey) =>
                  console.log(`selected ${selectedKey}`)
                }
                eventKey="test"
                className="pt-3"
                isAlignAtEnd
                isBlue
              >
                <span className="text-caption lh-xs">Tax Breakdown</span>
              </Link>
              <Link
                rightIcon={light('chevron-right')}
                onSelect={(selectedKey) =>
                  console.log(`selected ${selectedKey}`)
                }
                eventKey="test"
                className="pt-3"
                isAlignAtEnd
                isBlue
              >
                <span className="text-caption lh-xs">Min/Max Chart</span>
              </Link>
              <Link
                rightIcon={light('chevron-right')}
                onSelect={(selectedKey) =>
                  console.log(`selected ${selectedKey}`)
                }
                eventKey="test"
                className="pt-3"
                isAlignAtEnd
                isBlue
              >
                <span className="text-caption lh-xs">
                  Contribution Calculator
                </span>
              </Link>
            </div>
          </Support>
        </section>
      </div>
    </Container>
  );
};

export default SavingsAutoIncrease;
